import React, { useState, useEffect } from 'react';
import { IconButton } from '@nike/nike-design-system-components';
import { Pause, Play, CaretLeft, CaretRight } from '@nike/nike-design-system-icons';
import styled from '@emotion/styled';
import useInterval from '../hooks/useInterval';
import { array, string, number } from 'prop-types';

export const CarouselItem = styled.div`
  position: relative;
  display: none;
  transition: 0.6s ease-in-out left;
  transition: transform 0.6s ease-in-out;
  backface-visibility: hidden;
  perspective: 1000px;

  ${({ active }) =>
    active &&
    `left: 0;
    transform: translate3d(0, 0, 0);
    display: block;`}

  ${({ next, right }) =>
    next &&
    `left: 0;
    transform: translate3d(100%, 0, 0);
    position: absolute;
    top: 0;
    width: 100%;
    display: block;
    ${right && 'opacity: 0;'}`}

  ${({ prev, left }) =>
    prev &&
    `left: 0;
    transform: translate3d(-100%, 0, 0);
    position: absolute;
    top: 0;
    width: 100%;
    display: block;
    ${left && 'opacity: 0;'}`}
`;

const Carousel = ({ slides, height, timePerSlide }) => {
  const [indices, setIndices] = useState({ active: 0, prev: slides.length - 1, next: 1, right: true });
  const [isPaused, setIsPaused] = useState(false);

  const handleNextSlide = () => {
    if (indices.active === slides.length - 1) {
      return setIndices({ active: 0, prev: slides.length - 1, next: 1, right: true });
    }
    return setIndices({
      active: indices.active + 1,
      prev: indices.active,
      next: indices.next === slides.length - 1 ? 0 : indices.next + 1,
      right: true
    });
  };

  const handlePrevSlide = () => {
    if (indices.active === 0) {
      return setIndices({ active: slides.length - 1, prev: slides.length - 2, next: 0, right: false });
    }
    return setIndices({
      active: indices.active - 1,
      prev: indices.prev === 0 ? slides.length - 1 : indices.prev - 1,
      next: indices.active,
      right: false
    });
  };

  useInterval(() => {
    if (!isPaused) {
      handleNextSlide();
    }
  }, timePerSlide);

  useEffect(() => {
    setIndices({ active: 0, prev: slides.length - 1, next: 1, right: true });
  }, [slides]);

  return (
    <div style={{ position: 'relative', width: '100%', overflow: 'hidden' }}>
      {slides.length > 1 ? (
        <>
          <div style={{ height, boxShadow: '0px 1px 0px 0px #ddd' }}>
            {slides.map((slide, i) => (
              <CarouselItem
                active={i === indices.active}
                prev={i === indices.prev}
                next={i === indices.next}
                right={indices.right}
                left={!indices.right}
              >
                {slide}
              </CarouselItem>
            ))}
          </div>
          <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'center', marginTop: '1px' }}>
            <IconButton
              Icon={<CaretLeft />}
              appearance='tertiary'
              size='small'
              ariaLabel='Previous Slide'
              onClick={() => handlePrevSlide()}
            />
            <IconButton
              Icon={isPaused ? <Play /> : <Pause />}
              appearance='tertiary'
              size='small'
              ariaLabel='Pause Autoplay'
              onClick={() => setIsPaused(!isPaused)}
            />
            <IconButton
              Icon={<CaretRight />}
              appearance='tertiary'
              size='small'
              ariaLabel='Next Slide'
              onClick={() => handleNextSlide()}
            />
          </div>
        </>
      ) : (
        slides[0]
      )}
    </div>
  );
};

Carousel.propTypes = {
  slides: array.isRequired,
  height: string.isRequired,
  timePerSlide: number
};

Carousel.defaultProps = {
  timePerSlide: 5000
};

export default Carousel;
